import { useEffect } from 'react';
import {
  IonItem, IonItemDivider, IonLabel, IonNote, IonTextarea, IonCheckbox, IonButton,
  IonDatetimeButton, IonDatetime, IonModal
} from '@ionic/react';

import forceUnits from 'config/force-units';
import { toSignificant } from 'lib/number';
import { requiredInstallTorque } from 'lib/capacity-calculations';
import { getValue } from 'helpers/events';
import useModel from 'hooks/useModel';
import MagnumInput from 'components/helpers/MagnumInput';
import HelicalMeasurements from './HelicalMeasurements';
import { DEFAULT_HELICAL_LOG } from 'helpers/pileLogs';

const ftLbs = forceUnits["ftlbs"];

const DEFAULT_LOG = {
  pileNo: '',
  startTime: null,
  completionTime: null,
  pileShaft: '',
  pileLength: '',
  leadHelices: '',
  measurements: null,
  requiredTorque: '',
  finalTorque: '',
  anticipatedDepth: '',
  minimumDepth: '',
  finalDepth: '',
  aboveGround: '',
  criteriaMet: false,
  deviationNS: '',
  deviationEW: '',
  deviationV: '',
  plannedInclination: '',
  measuredInclination: '',
  comments: ''
};

const defaultFromLoad = (load, pileNumber) => ({
  ...DEFAULT_LOG,
  pileNo: pileNumber,
  pileShaft: load.pileOptions?.prefix,
  pileLength: load.pileOptions?.pileLength,
  leadHelices: load.pileOptions?.helixConfig?.join(),
  requiredTorque: load.groutedShaft ? 'n/a' : (
    (toSignificant(ftLbs.toDisplay(requiredInstallTorque(load)), 3) || '').toLocaleString()
  ),
  anticipatedDepth: (load.pileOptions?.pileLength || 0) - (load.aboveGround || 0) || '',
  aboveGround: (load.aboveGround || 0),
  plannedInclination: load.batteredPile ? load.batterAngle : 0
});

export default function HelicalLog({load, pileNumber, log, depthIncrement, updateLog})  {
  const [logFields, setLogsProperty, __, setState] = useModel(() => (
    _.pick(log || defaultFromLoad(load, pileNumber), _.keys(DEFAULT_HELICAL_LOG))
  ));

  useEffect(() => {
    if (log === null) setState(defaultFromLoad(load));
  }, [log]);

  const setLog = _.curry((key, value) => {
    if (!log) updateLog(logFields);
    setLogsProperty(key, value);
    updateLog({[key]: value});
  });

  return <>
    <IonItem>
      <MagnumInput label="Pile Number"
        labelPlacement="start"
        value={logFields.pileNo ?? pileNumber}
        onChange={getValue(setLog('pileNo'))}/>
    </IonItem>
    <IonItem>
      <IonLabel>
        Start Time
      </IonLabel>
      <IonDatetimeButton datetime="startTime" className={logFields.startTime === null && "blank-datetime"}/>
      <IonButton color="tertiary" onClick={() => setLog('startTime', new Date())}>Now</IonButton>

      <IonModal keepContentsMounted={true}>
        <IonDatetime id="startTime" value={logFields.startTime} onIonChange={getValue(setLog('startTime'))} showDefaultButtons/>
      </IonModal>
    </IonItem>
    <IonItem>
      <MagnumInput label="Pile Shaft Designation"
        labelPlacement="start"
        value={logFields.pileShaft}
        onChange={getValue(setLog('pileShaft'))}/>
    </IonItem>
    <IonItem>
      <MagnumInput label="Pile Length"
        labelPlacement="start"
        value={logFields.pileLength}
        onChange={getValue(setLog('pileLength'))}/>
    </IonItem>
    <IonItem>
      <MagnumInput label="Lead Helices"
        labelPlacement="start"
        value={logFields.leadHelices}
        onChange={getValue(setLog('leadHelices'))}/>
    </IonItem>

    <IonItemDivider>
      Periodic Torque Measurements
    </IonItemDivider>

    <HelicalMeasurements
      load={load}
      depthIncrement={depthIncrement}
      measurements={logFields.measurements}
      onChange={setLog('measurements')}/>

    <IonItemDivider>
      Termination Data
    </IonItemDivider>
    <IonItem>
      <IonLabel>
        Completion Time
      </IonLabel>
      <IonDatetimeButton datetime="completionTime" className={logFields.completionTime === null && "blank-datetime"}/>
      <IonButton color="tertiary" onClick={() => setLog('completionTime', new Date())}>Now</IonButton>

      <IonModal keepContentsMounted={true}>
        <IonDatetime id="completionTime" value={logFields.completionTime} onIonChange={getValue(setLog('completionTime'))} showDefaultButtons/>
      </IonModal>
    </IonItem>
    <IonItem>
      <MagnumInput label="Required Torque"
        labelPlacement="start"
        value={logFields.requiredTorque}
        onChange={getValue(setLog('requiredTorque'))}/>
        {ftLbs.abbreviation}
    </IonItem>
    <IonItem>
      <MagnumInput label="Final Torque"
        labelPlacement="start"
        value={logFields.finalTorque}
        onChange={getValue(setLog('finalTorque'))}/>
        {ftLbs.abbreviation}
    </IonItem>
    <IonItem>
      <MagnumInput label="Anticipated Depth"
        labelPlacement="start"
        value={logFields.anticipatedDepth}
        onChange={getValue(setLog('anticipatedDepth'))}/>
        ft
    </IonItem>
    <IonItem>
      <MagnumInput label="Minimum Depth"
        labelPlacement="start"
        value={logFields.minimumDepth}
        onChange={getValue(setLog('minimumDepth'))}/>
        ft
    </IonItem>
    <IonItem>
      <MagnumInput label="Final Depth"
        labelPlacement="start"
        value={logFields.finalDepth}
        onChange={getValue(setLog('finalDepth'))}/>
        ft
    </IonItem>
    <IonItem>
      <MagnumInput label="Above Ground"
        labelPlacement="start"
        value={logFields.aboveGround}
        onChange={getValue(setLog('aboveGround'))}/>
        ft
    </IonItem>
    <IonItem>
      <IonCheckbox
        labelPlacement="start"
        justify="space-between"
        checked={logFields.criteriaMet}
        onIonChange={getValue(setLog('criteriaMet'))}
      >
        <IonLabel>Criteria Met?</IonLabel>
        <IonNote color="medium" className="ion-text-wrap">
          Installed in general accordance with project documents.
        </IonNote>
      </IonCheckbox>
    </IonItem>

    <IonItemDivider>
      Deviations
    </IonItemDivider>
    <IonItem>
      <MagnumInput label="North-South Deviation"
        labelPlacement="start"
        value={logFields.deviationNS}
        onChange={getValue(setLog('deviationNS'))}/>
        in
    </IonItem>
    <IonItem>
      <MagnumInput label="East-West Deviation"
        labelPlacement="start"
        value={logFields.deviationEW}
        onChange={getValue(setLog('deviationEW'))}/>
        in
    </IonItem>
    <IonItem>
      <MagnumInput label="Vertical Deviation"
        labelPlacement="start"
        value={logFields.deviationV}
        onChange={getValue(setLog('deviationV'))}/>
        in
    </IonItem>
    <IonItem>
      <MagnumInput label="Planned Inclination Angle"
        labelPlacement="start"
        value={logFields.plannedInclination}
        onChange={getValue(setLog('plannedInclination'))}/>
    </IonItem>
    <IonItem>
      <MagnumInput label="Measured Inclination Angle"
        labelPlacement="start"
        value={logFields.measuredInclination}
        onChange={getValue(setLog('measuredInclination'))}/>
    </IonItem>
    <IonItemDivider></IonItemDivider>
    <IonItem>
      <IonTextarea label="Comments"
        labelPlacement="floating"
        rows={3}
        autoGrow
        value={logFields.comments}
        onIonInput={getValue(setLog('comments'))}/>
    </IonItem>
  </>
}
